/* Pricing.jsx — standalone /pricing page.
 * Renders the plan cards from config/plans.js and wires them to the real app:
 *   - Free (Starter) → into the app if signed in, else /signup.
 *   - Paid plan      → real Stripe Checkout (billingApi.startCheckout) if signed
 *                      in, else /signup first.
 *   - Already subscribed → NEVER Checkout (that makes a second subscription).
 *                      Opens PlanChangeDialog, which previews the exact charge
 *                      and waits for an explicit confirm.
 * The chosen plan + billing cycle are sent straight through to the backend,
 * which resolves the matching Stripe price (STRIPE_PRICE_PRO_MONTHLY/ANNUAL,
 * STRIPE_PRICE_ELITE_MONTHLY/ANNUAL — see stripeService.js resolvePriceId
 * and engineering/billing/JAI_STRIPE.md). Each tier + billing cycle charges its own price
 * once those four env vars are set. */

import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { JaiMark, JaiWordmark, JAI_COLORS, JAI_FONTS } from '../components/JaiBrand'
import { ArrowLeft, ErrorBanner } from '../components/AuthFields'
import PlanPicker from '../components/plans/PlanPicker'
import PlanChangeDialog from '../components/plans/PlanChangeDialog'
import TopupPacks from '../components/TopupPacks'
import { CREDITS_EXPLAINER } from '../config/plans'
import { useAuth } from '../context/AuthContext'
import { billingApi } from '../services/api'
import { useBackToApp } from '../utils/appReturn'
import { rememberPendingCheckout } from '../utils/pendingCheckout'

export default function Pricing() {
  const navigate = useNavigate()
  const { hash } = useLocation()
  const { user, token, isGuest, plan: currentPlan, planInterval, scheduledChange } = useAuth()

  // A guest counts as signed OUT here. They hold a session, but not one that
  // can own a subscription: their email is an internal placeholder, so Stripe
  // would have nowhere to send a receipt and no way to warn them before the
  // trial converts. They go through sign-up first, exactly like a visitor with
  // no session at all. (The backend refuses guest checkouts too — see
  // startCheckout in billingController.js.)
  const signedIn = !!(user || token) && !isGuest

  // Signed in → back to the screen they left (they came here from inside the
  // app, e.g. Upgrade plan); signed out → the marketing landing page.
  const back = useBackToApp()

  // Annual first. It's the better deal for the user (31% off Pro, 13% off
  // Elite vs. 12 months of monthly — see annualSavingsLabel in config/plans.js)
  // and the better outcome for JAI — a year of runway up front instead of a
  // month, and far less churn to manage. The toggle is right there for anyone
  // who wants to pay monthly.
  const [billing, setBilling] = useState('annual')
  const [busyPlanId, setBusyPlanId] = useState('')
  const [error, setError] = useState('')
  const [changing, setChanging] = useState(null) // { plan, billing } while the confirm dialog is open

  // /pricing#credits (from the out-of-credits dialog) lands on the top-up
  // section. The router does not scroll to a hash on its own.
  useEffect(() => {
    if (hash !== '#credits') return
    const t = setTimeout(() => document.getElementById('credits')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150)
    return () => clearTimeout(t)
  }, [hash])

  // Already on a paid plan: a different pick edits that subscription.
  const hasSubscription = signedIn && !!currentPlan && currentPlan !== 'starter'

  const handleSelect = async (plan) => {
    setError('')

    // Free tier — no payment. Straight into the app (or signup first).
    if (plan.free) {
      navigate(signedIn ? '/chat' : '/signup')
      return
    }

    // Paid tier — needs a real account, then Stripe's hosted checkout.
    // Remember which plan they picked before sending them off to sign up:
    // without this, Signup drops them straight into the app with no memory of
    // the plan they chose, and they have to go find "Upgrade plan" and pick
    // again. Signup/Login check for this and resume checkout on success.
    if (!signedIn) {
      await rememberPendingCheckout(plan.id, billing)
      navigate('/signup')
      return
    }

    // Someone who already subscribes changes their existing subscription —
    // preview, then explicit confirm — rather than starting a second one.
    if (hasSubscription) {
      setChanging({ plan, billing })
      return
    }

    setBusyPlanId(plan.id)
    try {
      const { data } = await billingApi.startCheckout(plan.id, billing)
      window.location.href = data.url // hand off to Stripe
    } catch (err) {
      // Our idea of their plan was stale (e.g. subscribed in another tab): the
      // server says they already have one, so this is a change after all.
      if (err.response?.data?.code === 'SUBSCRIPTION_EXISTS') {
        setBusyPlanId('')
        setChanging({ plan, billing })
        return
      }
      setError(err.response?.data?.error || 'Could not start checkout. Please try again.')
      setBusyPlanId('')
    }
  }

  return (
    <div style={{ minHeight: 'calc(100vh / var(--jai-zoom))', background: JAI_COLORS.paper, fontFamily: JAI_FONTS.tight, display: 'flex', flexDirection: 'column' }}>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '16px 26px', borderBottom: '1px solid rgba(10,31,68,0.07)', background: 'rgba(246,248,251,0.85)', backdropFilter: 'blur(10px)', position: 'sticky', top: 0, zIndex: 20 }}>
        <button type="button" onClick={() => navigate(back.to)} title={back.label}
          style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <JaiMark size={30} />
          <JaiWordmark size={19} />
        </button>
        <button type="button" onClick={() => navigate(back.to)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, border: '1px solid rgba(10,31,68,0.18)', borderRadius: 999, padding: '7px 14px 7px 11px', cursor: 'pointer', color: JAI_COLORS.mute, fontSize: 12.5, fontWeight: 600, fontFamily: JAI_FONTS.tight }}>
          <ArrowLeft size={14} /> {back.label}
        </button>
      </header>

      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: '44px 24px 64px' }}>
        {error && (
          <div style={{ width: '100%', maxWidth: 460, marginBottom: 8 }}>
            <ErrorBanner>{error}</ErrorBanner>
          </div>
        )}
        {/* currentPlan/currentInterval are what stop someone being sold the
            plan they're already on. Passed as null for guests and signed-out
            visitors: they have no subscription, so every card is open to them.
            (A guest's `plan` reads 'starter' — real access, but not something
            to mark as a "current plan" on this page.) */}
        <PlanPicker
          billing={billing}
          setBilling={setBilling}
          onSelect={handleSelect}
          busyPlanId={busyPlanId}
          currentPlan={signedIn ? currentPlan : null}
          currentInterval={signedIn ? planInterval : null}
          hasScheduledChange={!!scheduledChange}
        />
      </main>

      {/* Credit top-ups. Linked to from the out-of-credits dialog as /pricing#credits.
          The page is not registered in the iOS app, and TopupPacks renders
          nothing there either. Guests are sent to sign up first (the server
          refuses a guest top-up too: no email to send the receipt to). */}
      <section id="credits" style={{ width: '100%', maxWidth: 640, margin: '0 auto', padding: '0 24px 48px', boxSizing: 'border-box' }}>
        <h2 style={{ fontSize: 20, fontWeight: 780, color: JAI_COLORS.navy, margin: '0 0 6px', letterSpacing: '-0.02em' }}>Need more credits?</h2>
        <p style={{ fontSize: 13.5, color: JAI_COLORS.mute, margin: '0 0 14px', lineHeight: 1.55 }}>
          {CREDITS_EXPLAINER} Top-ups work on any plan, including Starter.
        </p>
        <TopupPacks signedIn={signedIn} onNeedAccount={() => navigate('/signup')} />
      </section>

      {changing && (
        <PlanChangeDialog
          plan={changing.plan}
          billing={changing.billing}
          onClose={() => setChanging(null)}
          onDone={() => { setChanging(null); navigate(back.to) }}
        />
      )}

      <footer style={{ textAlign: 'center', padding: '0 0 22px' }}>
        <span style={{ fontSize: 11.5, color: 'rgba(10,31,68,0.40)', fontFamily: JAI_FONTS.tight }}>© 2026 JAI · Smarter fantasy picks</span>
      </footer>
    </div>
  )
}
